/**
 * Copyright (c) 2018-2023 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { __assign } from "tslib";
import { ParamDefinition as PD } from '../../../mol-util/param-definition';
import { Vec3, Mat4 } from '../../../mol-math/linear-algebra';
import { Box } from '../../../mol-geo/primitive/box';
import { Unit } from '../../../mol-model/structure';
import { Mesh } from '../../../mol-geo/geometry/mesh/mesh';
import { MeshBuilder } from '../../../mol-geo/geometry/mesh/mesh-builder';
import { Segmentation } from '../../../mol-data/int';
import { isNucleic } from '../../../mol-model/structure/model/types';
import { addCylinder } from '../../../mol-geo/geometry/mesh/builder/cylinder';
import { UnitsMeshParams, UnitsMeshVisual } from '../units-visual';
import { NucleotideLocationIterator, getNucleotideElementLoci, eachNucleotideElement, getNucleotideBaseType, createNucleicIndices, setPurinIndices, setPyrimidineIndices } from './util/nucleotide';
import { BaseGeometry } from '../../../mol-geo/geometry/base';
import { Sphere3D } from '../../../mol-math/geometry';
// TODO support blocks for multiple locations (including from microheterogeneity)
var p1 = Vec3();
var p2 = Vec3();
var p3 = Vec3();
var p4 = Vec3();
var p5 = Vec3();
var pt = Vec3();
var v12 = Vec3();
var v34 = Vec3();
var vC = Vec3();
var center = Vec3();
var t = Mat4.identity();
var sVec = Vec3();
var box = Box();
export var NucleotideBlockMeshParams = {
    sizeFactor: PD.Numeric(0.2, { min: 0, max: 10, step: 0.01 }),
    thicknessFactor: PD.Numeric(1, { min: 0, max: 2, step: 0.01 }),
    radialSegments: PD.Numeric(16, { min: 2, max: 56, step: 2 }, BaseGeometry.CustomQualityParamInfo),
};
export var DefaultNucleotideBlockMeshProps = PD.getDefaultValues(NucleotideBlockMeshParams);
function createNucleotideBlockMesh(ctx, unit, structure, theme, props, mesh) {
    if (!Unit.isAtomic(unit))
        return Mesh.createEmpty(mesh);
    var nucleotideElementCount = unit.nucleotideElements.length;
    if (!nucleotideElementCount)
        return Mesh.createEmpty(mesh);
    var sizeFactor = props.sizeFactor, thicknessFactor = props.thicknessFactor, radialSegments = props.radialSegments;
    var vertexCount = nucleotideElementCount * (box.vertices.length / 3 + radialSegments * 2);
    var builderState = MeshBuilder.createState(vertexCount, vertexCount / 4, mesh);
    var elements = unit.elements, model = unit.model;
    var _a = model.atomicHierarchy, chainAtomSegments = _a.chainAtomSegments, residueAtomSegments = _a.residueAtomSegments;
    var moleculeType = model.atomicHierarchy.derived.residue.moleculeType;
    var pos = unit.conformation.invariantPosition;
    var chainIt = Segmentation.transientSegments(chainAtomSegments, elements);
    var residueIt = Segmentation.transientSegments(residueAtomSegments, elements);
    var radius = 1 * sizeFactor;
    var width = 4.5;
    var depth = thicknessFactor * sizeFactor * 2;
    var cylinderProps = { radiusTop: radius, radiusBottom: radius, radialSegments: radialSegments, bottomCap: true };
    var i = 0;
    while (chainIt.hasNext) {
        residueIt.setSegment(chainIt.move());
        while (residueIt.hasNext) {
            var residueIndex = residueIt.move().index;
            if (isNucleic(moleculeType[residueIndex])) {
                var idx = createNucleicIndices();
                var idx1 = -1, idx2 = -1, idx3 = -1, idx4 = -1, idx5 = -1;
                var height = 4.5;
                var _b = getNucleotideBaseType(unit, residueIndex), isPurine = _b.isPurine, isPyrimidine = _b.isPyrimidine;
                if (isPurine) {
                    height = 4.5;
                    setPurinIndices(idx, unit, residueIndex);
                    idx1 = idx.N1;
                    idx2 = idx.C4;
                    idx3 = idx.C6;
                    idx4 = idx.C2;
                    idx5 = idx.N9;
                }
                else if (isPyrimidine) {
                    height = 3.0;
                    setPyrimidineIndices(idx, unit, residueIndex);
                    idx1 = idx.N3;
                    idx2 = idx.C6;
                    idx3 = idx.C4;
                    idx4 = idx.C2;
                    idx5 = idx.N1;
                }
                if (idx5 !== -1 && idx.trace !== -1) {
                    pos(idx5, p5);
                    pos(idx.trace, pt);
                    builderState.currentGroup = i;
                    addCylinder(builderState, p5, pt, 1, cylinderProps);
                    if (idx1 !== -1 && idx2 !== -1 && idx3 !== -1 && idx4 !== -1) {
                        pos(idx1, p1);
                        pos(idx2, p2);
                        pos(idx3, p3);
                        pos(idx4, p4);
                        Vec3.normalize(v12, Vec3.sub(v12, p2, p1));
                        Vec3.normalize(v34, Vec3.sub(v34, p4, p3));
                        Vec3.normalize(vC, Vec3.cross(vC, v12, v34));
                        Mat4.targetTo(t, p1, p2, vC);
                        Vec3.scaleAndAdd(center, p1, v12, height / 2 - 0.2);
                        Mat4.scale(t, t, Vec3.set(sVec, width, depth, height));
                        Mat4.setTranslation(t, center);
                        MeshBuilder.addPrimitive(builderState, t, box);
                    }
                }
                ++i;
            }
        }
    }
    var m = MeshBuilder.getMesh(builderState);
    var sphere = Sphere3D.expand(Sphere3D(), unit.boundary.sphere, radius);
    m.setBoundingSphere(sphere);
    return m;
}
export var NucleotideBlockParams = __assign(__assign({}, UnitsMeshParams), NucleotideBlockMeshParams);
export function NucleotideBlockVisual(materialId) {
    return UnitsMeshVisual({
        defaultProps: PD.getDefaultValues(NucleotideBlockParams),
        createGeometry: createNucleotideBlockMesh,
        createLocationIterator: NucleotideLocationIterator.fromGroup,
        getLoci: getNucleotideElementLoci,
        eachLocation: eachNucleotideElement,
        setUpdateState: function (state, newProps, currentProps) {
            state.createGeometry = (newProps.sizeFactor !== currentProps.sizeFactor ||
                newProps.thicknessFactor !== currentProps.thicknessFactor ||
                newProps.radialSegments !== currentProps.radialSegments);
        }
    }, materialId);
}
